// src/firebase.js
// Centralized Firebase client: App, Auth, Firestore, Functions
// - Safe to import anywhere in the app
// - Includes optional local emulator support via REACT_APP_* env flags

import { initializeApp, getApps } from "firebase/app";
import { getAuth, setPersistence, browserLocalPersistence } from "firebase/auth";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
//import "./debug/cfGuards"; // side-effect: installs the guard

// Prefer Vite's import.meta.env, fallback to process.env (CRA)
const ENV = typeof import.meta !== "undefined" && import.meta.env ? import.meta.env : process.env;

// --- Your Firebase project config (from Console) ---
const firebaseConfig = {
  apiKey: "AIzaSyACG-6_P4z8kBNnwwEx0j2xnV-_V7yCogE",
  authDomain: "trapound-app.firebaseapp.com",
  projectId: "trapound-app",
  storageBucket: "trapound-app.firebasestorage.app",
  messagingSenderId: "782608246463",
  appId: "1:782608246463:web:586bf564242a1ccfe4080a",
  measurementId: "G-B7DMM380RM",
};



function assertConfig(cfg) {
  const missing = Object.entries(cfg)
    .filter(([, v]) => !v)
    .map(([k]) => k);
  if (missing.length) {
    // eslint-disable-next-line no-console
    console.warn("[firebase] Missing config keys:", missing.join(", "));
  }
}
assertConfig(firebaseConfig);

// // Install runtime guards early
// if (typeof window !== "undefined") {
//   installCloudFunctionsGuards();
// }

// --- Initialize (avoid duplicate apps in HMR) ---
export const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);

// --- Auth (persist session across tabs/windows) ---
export const auth = getAuth(app);
if (typeof window !== "undefined") window._auth = auth;
setPersistence(auth, browserLocalPersistence).catch(() => {});

// --- Firestore ---
export const db = getFirestore(app);

// --- Functions (make sure region matches your deploy, e.g. 'us-central1') ---
export const functions = getFunctions(app, "us-central1");


// // Local emulator only on localhost (keep this guard tight)
// if (typeof window !== "undefined" && window.location.hostname === "localhost") {
//   connectFunctionsEmulator(functions, "localhost", 5001);
// }



// 🔎 Debug wrapper you can import in components
export const fbCallableDebug = (name) => {
  const fn = httpsCallable(functions, name);
  return async (data) => {
    const origin = typeof window !== "undefined" ? window.location.origin : "server";
    console.info(
      `%c[CALLABLE→] ${name}`,
      "color:#0af",
      { origin, data }
    );
    try {
      const res = await fn(data);
      console.info(
        `%c[CALLABLE←] ${name}`,
        "color:#0a0",
        { data: res?.data }
      );
      return res;
    } catch (err) {
      console.error(
        `%c[CALLABLE×] ${name}`,
        "color:#d00",
        err
      );
      throw err;
    }
  };
};

const isBrowser = typeof window !== "undefined";
// const isLocalhost =
//   isBrowser &&
//   (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

// if (isBrowser) {
//   // Dev: no captchas. Prod: real v3 site key (invisible)
//   // In dev, this line tells Firebase to use a debug token instead of a captcha.
//   window.FIREBASE_APPCHECK_DEBUG_TOKEN = isLocalhost ? true : undefined;

//   initializeAppCheck(app, {
//     provider: new ReCaptchaV3Provider(import.meta.env.VITE_RECAPTCHA_V3_SITE_KEY || "unused-in-debug"),
//     isTokenAutoRefreshEnabled: true,
//   });
// }

// // Local emulator only (no CORS headaches during dev)
// if (isLocalhost) {
//   connectFunctionsEmulator(functions, "localhost", 5001);
// }




// // ============ Optional: Emulator support for local dev ============
// // Firestore emulator
// const USE_FS_EMU = ENV.REACT_APP_USE_FIREBASE_EMULATORS === "true";
// if (USE_FS_EMU) {
//   const host = ENV.REACT_APP_FIRESTORE_EMULATOR_HOST || "localhost";
//   const port = Number(ENV.REACT_APP_FIRESTORE_EMULATOR_PORT || 8080);
//   try {
//     connectFirestoreEmulator(db, host, port);
//     // eslint-disable-next-line no-console
//     console.info(`[firebase] Firestore emulator connected at ${host}:${port}`);
//   } catch (e) {
//     // eslint-disable-next-line no-console
//     console.warn("[firebase] Failed to connect Firestore emulator:", e?.message || e);
//   }
// }

// // Functions emulator
// const USE_FN_EMU = ENV.REACT_APP_USE_FUNCTIONS_EMULATOR === "true";
// if (USE_FN_EMU) {
//   const host = ENV.REACT_APP_FUNCTIONS_EMULATOR_HOST || "localhost";
//   const port = Number(ENV.REACT_APP_FUNCTIONS_EMULATOR_PORT || 5001);
//   try {
//     connectFunctionsEmulator(functions, host, port);
//     // eslint-disable-next-line no-console
//     console.info(`[firebase] Functions emulator connected at ${host}:${port}`);
//   } catch (e) {
//     // eslint-disable-next-line no-console
//     console.warn("[firebase] Failed to connect Functions emulator:", e?.message || e);
//   }
// }
